import {
    ModalSearchBase,
    type ModalSearchSelectionMode,
} from "../../../components/modal-search/modal-search-base-handler";
import { SearchUserModalApi } from "./search-user-modal-api";
import { SearchUserModalRender, type SearchUserModalItem } from "./search-user-modal-render";
import { translator } from "../../../translation/translator";
import commonUtil from "../../../util/common-utils";
import { Actions } from "../../../constants/actions";
import { UserDisplayHelper } from "../../../user/user-display-utils";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

async function toUserItem(raw: unknown): Promise<SearchUserModalItem | null> {
    if (!isRecord(raw)) return null;

    const normalized = await UserDisplayHelper.normalizeUserItem(raw);
    if (!normalized) return null;

    return {
        id: normalized.id,
        displayName: normalized.displayName,
        reviewCount: normalized.reviewCount,
        followersCount: normalized.followersCount,
        isFollowing: normalized.isFollowing,
    };
}

function isSearchUserModalItem(item: SearchUserModalItem | null): item is SearchUserModalItem {
    return item !== null;
}

function matchesUserPrefix(item: SearchUserModalItem, query: string): boolean {
    const normalizedQuery = String(query || "")
        .trim()
        .toLowerCase();
    if (normalizedQuery === "") {
        return true;
    }

    const displayName = String(item.displayName || "")
        .replace(/^@/, "")
        .trim()
        .toLowerCase();
    const id = String(item.id || "")
        .trim()
        .toLowerCase();

    return displayName.startsWith(normalizedQuery) || id.startsWith(normalizedQuery);
}

export interface SearchUserModalHandlerParams {
    overlayEl: HTMLElement;
    modalEl: HTMLElement;
    listRootEl: HTMLElement;
    filterInputEl: HTMLInputElement | null;
    selectionMode?: ModalSearchSelectionMode;
    closeOnSelect?: boolean;
}

export interface SearchUserModalSelectionOptions {
    key?: string;
}

export class SearchUserModalHandler {
    public listRootEl: HTMLElement;
    public cacheByQuery: Map<string, SearchUserModalItem[]>;
    public items: SearchUserModalItem[];
    public isLoading: boolean;
    private _selectionChangeByKey: Map<string, (ids: string[]) => void>;
    private _loadSeq: number;
    private _lastRequestedQuery: string | null;
    public base: ModalSearchBase;

    public constructor(params: SearchUserModalHandlerParams) {
        this.listRootEl = params.listRootEl;
        this._selectionChangeByKey = this.createSelectionChangeMap();
        this.cacheByQuery = this.createCacheByQueryMap();
        this.items = this.createUserItemsSlot();
        this.isLoading = false;
        this._loadSeq = 0;
        this._lastRequestedQuery = null;

        this.base = new ModalSearchBase({
            overlayEl: params.overlayEl,
            modalEl: params.modalEl,
            filterInputEl: params.filterInputEl,
            adapter: {
                render: (base) => {
                    this.ensureLoaded(String(base.query || ""));

                    SearchUserModalRender.render({
                        listRoot: this.listRootEl,
                        items: this.items,
                        selectedIds: base.selectedIds,
                        isLoading: this.isLoading,
                    });

                    translator.updateUITranslations();
                },
                handleAction: (base, action, actionEl) => {
                    if (action !== Actions.SELECT_USER) return false;
                    const id = String(actionEl.getAttribute("data-user-id") || "").trim();
                    if (!id) return true;
                    base.toggleSelectedId(id);
                    base.render();
                    return true;
                },
            },
        });

        const selectionMode = params.selectionMode === "single" ? "single" : "multi";
        const closeOnSelect = typeof params.closeOnSelect === "boolean" ? params.closeOnSelect : false;

        this.base.setSelectionMode(selectionMode);
        this.base.setCloseOnSelect(closeOnSelect);

        this.base.setOnSelectionChange((ids) => {
            for (const callback of this._selectionChangeByKey.values()) {
                try {
                    callback(ids);
                } catch (error) {
                    console.warn("[SearchUserModal] onSelectionChange failed", error);
                }
            }
        });
    }

    public createSelectionChangeMap(): Map<string, (ids: string[]) => void> {
        return new Map();
    }

    public createCacheByQueryMap(): Map<string, SearchUserModalItem[]> {
        return new Map();
    }

    public createUserItemsSlot(): SearchUserModalItem[] {
        return [];
    }

    public ensureLoaded(query: string): void {
        const normalizedQuery = String(query || "").trim();

        if (this.cacheByQuery.has(normalizedQuery)) {
            this.items = this.cacheByQuery.get(normalizedQuery) || [];
            this.isLoading = false;
            this._lastRequestedQuery = normalizedQuery;
            return;
        }

        if (this.isLoading && this._lastRequestedQuery === normalizedQuery) {
            return;
        }

        this._lastRequestedQuery = normalizedQuery;
        this.isLoading = true;

        const seq = ++this._loadSeq;

        setTimeout(async () => {
            if (seq !== this._loadSeq) return;

            try {
                const json = await SearchUserModalApi.searchUsers(normalizedQuery);

                const data = Array.isArray(json.data) ? json.data : [];

                const items = (await Promise.all(data.map((entry) => toUserItem(entry))))
                    .filter(isSearchUserModalItem)
                    .filter((item) => matchesUserPrefix(item, normalizedQuery));
                this.cacheByQuery.set(normalizedQuery, items);

                if (seq !== this._loadSeq) return;
                this.items = items;
            } catch (error) {
                this.cacheByQuery.set(normalizedQuery, []);
                if (seq !== this._loadSeq) return;
                this.items = [];
                console.warn("[SearchUserModal] Failed to load users", error);
            } finally {
                const isLatest = seq === this._loadSeq;
                if (isLatest) {
                    this.isLoading = false;
                    this.base.render();
                }
            }
        }, 250);
    }

    public setSelectedIds(ids: string[]): void {
        this.base.setSelectedIds(ids);
    }

    public getSelectedIds(): string[] {
        return this.base.getSelectedIds();
    }

    public setOnSelectionChange(
        callback: ((ids: string[]) => void) | null,
        options: SearchUserModalSelectionOptions = {},
    ): void {
        const key = String(options.key || "default");
        if (typeof callback !== "function") {
            this._selectionChangeByKey.delete(key);
            return;
        }
        this._selectionChangeByKey.set(key, callback);
    }

    public getLabelMap(): Map<string, string> {
        const map = new Map<string, string>();
        for (const list of this.cacheByQuery.values()) {
            (Array.isArray(list) ? list : []).forEach((user) => {
                if (!user?.id) return;

                const label = commonUtil.escapeHtml(String(user.displayName || user.id));
                const html = `${label} <span class="picker-user-meta">${UserDisplayHelper.buildUserMetaHtml({
                    reviewCount: Number.isFinite(Number(user.reviewCount)) ? Number(user.reviewCount) : 0,
                    followersCount: Number.isFinite(Number(user.followersCount)) ? Number(user.followersCount) : 0,
                })}</span>`;
                map.set(String(user.id), html);
            });
        }
        return map;
    }

    public open(): void {
        this.base.open();
    }

    public close(): void {
        this.base.close();
    }

    public getBase(): ModalSearchBase {
        return this.base;
    }
}
